import { makeAutoObservable, runInAction } from 'mobx'
import { AxiosError } from 'axios'
import { getCDNAssetURL, logger } from 'shared/lib'
import { type INormalizedApiError } from 'shared/api'
import optInApproved from 'shared/assets/images/rcsCompliance/optInApproved.png'
import { Attachment } from 'entities/Attachment/model/Attachment'
import { type IEntitiesStore } from 'entities/EntitiesExport'
import { type IResponseRcsCompliance, type IRcsMessageInfoStepParams, RcsApi } from 'entities/Rcs'
import { type PresentationModeStore } from 'widgets/PresentationMode'
import { applyOrganizationNamePlaceholder } from 'pages/settings/pages/rcsV2/lib/applyOrganizationNamePlaceholder'
import {
  RCS_ORGANIZATION_NAME_FALLBACK,
  RCS_SAMPLE_HELP_TEMPLATE,
  RCS_SAMPLE_OPT_OUT_TEMPLATE,
} from 'pages/settings/pages/rcsV2/lib/sampleMessageTemplates'
import { type RcsWizardStore } from 'pages/settings/pages/rcsV2/store/rcsWizardStore'

const MESSAGE_FLOW_MIN_LENGTH = 40
const MESSAGE_FLOW_MAX_LENGTH = 1000
const OPT_OUT_MIN_LENGTH = 20
const OPT_OUT_MAX_LENGTH = 1000
const SAMPLE_MESSAGE_MAX_LENGTH = 4096
const SAMPLE_SEGMENT_WARNING_THRESHOLD = 160
const SAMPLE_SEGMENT_WARNING = 'Messages over 160 chars are split into multiple SMS segments.'
const IMAGE_MAX_SIZE_BYTES = 10 * 1024 * 1024
const IMAGE_ALLOWED_TYPES = ['image/jpeg', 'image/png']
const IMAGE_ALLOWED_EXTENSIONS = ['jpg', 'jpeg', 'png']
const IMAGE_TYPE_ERROR = {
  title: 'The file is not supported for upload',
  desc: 'Please upload only: jpg, png',
}
const IMAGE_SIZE_ERROR = {
  desc: 'The image file is too large. It must not exceed 10 MB',
}
const IMAGE_UPLOAD_ERROR = {
  desc: 'Failed to upload the opt-in image',
}
const IMAGE_LIMIT_ERROR = {
  desc: 'You can upload only 1 image. Remove the current one first.',
}

type IImageError = { title?: string; desc: string }

export class OptInMessageInfoStore {
  private _messageFlow = ''
  private _messageFlowError = ''
  private _optInImage: File | null = null
  private _optInImageUrl = ''
  private _optInImageError: IImageError | null = null
  private _optInImageLoading = false
  private _optOut = ''
  private _optOutError = ''
  private _sampleMessage = ''
  private _sampleMessageError = ''
  private _sampleOptOut = ''
  private _sampleOptOutError = ''

  constructor(
    private _wizardStore: RcsWizardStore,
    private _presentationStore: PresentationModeStore,
    private _entities: IEntitiesStore
  ) {
    makeAutoObservable(this)
  }

  openOptInImageExample = () => {
    this._presentationStore.setAttachments([
      new Attachment({
        imageUrl: getCDNAssetURL(optInApproved),
        name: 'Opt-in / Online Form / Approved example',
      }),
    ])
  }

  get messageFlow() {
    return this._messageFlow
  }

  get messageFlowError() {
    return this._messageFlowError
  }

  get optInImage() {
    return this._optInImage
  }

  get optInImageUrl() {
    return this._optInImageUrl
  }

  get optInImageError() {
    return this._optInImageError
  }

  get optInImageLoading() {
    return this._optInImageLoading
  }

  get optOut() {
    return this._optOut
  }

  get optOutError() {
    return this._optOutError
  }

  get isUsFlow() {
    return this._wizardStore.isUsFlow
  }

  get sampleMessage() {
    return this._sampleMessage
  }

  get sampleMessageError() {
    return this._sampleMessageError
  }

  get sampleOptOut() {
    return this._sampleOptOut
  }

  get sampleOptOutError() {
    return this._sampleOptOutError
  }

  get sampleMessageMaxLength() {
    return SAMPLE_MESSAGE_MAX_LENGTH
  }

  get organizationName() {
    return this._entities.organizationStore.name || RCS_ORGANIZATION_NAME_FALLBACK
  }

  get sampleMessagePlaceholder() {
    return applyOrganizationNamePlaceholder(RCS_SAMPLE_HELP_TEMPLATE, this.organizationName)
  }

  get sampleOptOutPlaceholder() {
    return applyOrganizationNamePlaceholder(RCS_SAMPLE_OPT_OUT_TEMPLATE, this.organizationName)
  }

  get sampleMessageSegmentWarning() {
    return this._sampleMessage.length > SAMPLE_SEGMENT_WARNING_THRESHOLD
      ? SAMPLE_SEGMENT_WARNING
      : ''
  }

  get sampleOptOutSegmentWarning() {
    return this._sampleOptOut.length > SAMPLE_SEGMENT_WARNING_THRESHOLD
      ? SAMPLE_SEGMENT_WARNING
      : ''
  }

  get messageFlowMinLength() {
    return MESSAGE_FLOW_MIN_LENGTH
  }

  get messageFlowMaxLength() {
    return MESSAGE_FLOW_MAX_LENGTH
  }

  get optOutMinLength() {
    return OPT_OUT_MIN_LENGTH
  }

  get optOutMaxLength() {
    return OPT_OUT_MAX_LENGTH
  }

  private get _isMessageFlowValid() {
    const { length } = this._messageFlow
    return length >= MESSAGE_FLOW_MIN_LENGTH && length <= MESSAGE_FLOW_MAX_LENGTH
  }

  private get _isOptOutValid() {
    const { length } = this._optOut
    return length >= OPT_OUT_MIN_LENGTH && length <= OPT_OUT_MAX_LENGTH
  }

  private get _isSampleMessageValid() {
    return !this.isUsFlow || this._sampleMessage.trim().length > 0
  }

  private get _isSampleOptOutValid() {
    return !this.isUsFlow || this._sampleOptOut.trim().length > 0
  }

  get params(): IRcsMessageInfoStepParams {
    const params: IRcsMessageInfoStepParams = {
      opt_in_description: this._messageFlow,
      opt_in_image_url: this._optInImageUrl,
      opt_out_description: this._optOut,
    }

    if (!this.isUsFlow) return params

    return {
      ...params,
      sample_message: this._sampleMessage || undefined,
      sample_opt_out_message: this._sampleOptOut || undefined,
    }
  }

  get isNextDisabled() {
    return !(
      this._isMessageFlowValid &&
      this._optInImageUrl &&
      this._isOptOutValid &&
      this._isSampleMessageValid &&
      this._isSampleOptOutValid
    )
  }

  setMessageFlow = (value: string) => {
    this._messageFlow = value
    if (this._messageFlowError) this._messageFlowError = ''
  }

  setOptOut = (value: string) => {
    this._optOut = value
    if (this._optOutError) this._optOutError = ''
  }

  setSampleMessage = (value: string) => {
    this._sampleMessage = value
    if (this._sampleMessageError) this._sampleMessageError = ''
  }

  setSampleOptOut = (value: string) => {
    this._sampleOptOut = value
    if (this._sampleOptOutError) this._sampleOptOutError = ''
  }

  setOptInImage = (files: File[] | FileList) => {
    const [file] = Array.from(files)
    if (!file) return

    if (this._optInImage || this._optInImageUrl) {
      this._optInImageError = IMAGE_LIMIT_ERROR
      return
    }

    const error = this._getImageError(file)
    if (error) {
      this._optInImageError = error
      return
    }

    this._optInImageError = null
    this._optInImage = file
    this._uploadOptInImage(file)
  }

  removeOptInImage = () => {
    this._optInImage = null
    this._optInImageUrl = ''
    this._optInImageError = null
    this._optInImageLoading = false
  }

  clearOptInImageError = () => {
    this._optInImageError = null
  }

  private _uploadOptInImage = async (file: File) => {
    runInAction(() => {
      this._optInImageLoading = true
    })

    try {
      const formData = new FormData()
      formData.append('file', file)

      const {
        data: { url },
      } = await RcsApi.uploadOptInImage(formData)

      runInAction(() => {
        if (this._optInImage !== file) return

        this._optInImageUrl = url
        this._optInImageLoading = false
      })
    } catch (error) {
      logger.error(error)

      const fileError = error instanceof AxiosError ? error.errors.getError('file') : ''

      runInAction(() => {
        if (this._optInImage !== file) return

        this._optInImage = null
        this._optInImageError = fileError ? { desc: fileError } : IMAGE_UPLOAD_ERROR
        this._optInImageLoading = false
      })
    }
  }

  private _getImageError = (file: File): IImageError | null => {
    if (!this._isImageTypeAllowed(file)) return IMAGE_TYPE_ERROR
    if (file.size > IMAGE_MAX_SIZE_BYTES) return IMAGE_SIZE_ERROR
    return null
  }

  private _isImageTypeAllowed = (file: File) => {
    if (IMAGE_ALLOWED_TYPES.includes(file.type)) return true

    const extension = file.name.split('.').pop()?.toLowerCase()
    return extension ? IMAGE_ALLOWED_EXTENSIONS.includes(extension) : false
  }

  initErrors = (errors: INormalizedApiError) => {
    this._messageFlowError = errors.getError('opt_in_description')
    this._optOutError = errors.getError('opt_out_description')

    const imageError = errors.getError('opt_in_image_url')
    this._optInImageError = imageError ? { desc: imageError } : null

    this._sampleMessageError = errors.getError('sample_message')
    this._sampleOptOutError = errors.getError('sample_opt_out_message')
  }

  init = (data: IResponseRcsCompliance) => {
    const messageInfo = data.data?.message_info
    if (!messageInfo) return

    this._messageFlow = messageInfo.opt_in_description ?? ''
    this._optInImageUrl = messageInfo.opt_in_image_url ?? ''
    this._optOut = messageInfo.opt_out_description ?? ''
    this._sampleMessage = messageInfo.sample_message ?? ''
    this._sampleOptOut = messageInfo.sample_opt_out_message ?? ''
  }

  reset = () => {
    this._messageFlow = ''
    this._messageFlowError = ''
    this._optInImage = null
    this._optInImageUrl = ''
    this._optInImageError = null
    this._optInImageLoading = false
    this._optOut = ''
    this._optOutError = ''
    this._sampleMessage = ''
    this._sampleMessageError = ''
    this._sampleOptOut = ''
    this._sampleOptOutError = ''
  }
}
